import { createMemo, For, type JSX, Show } from "solid-js";
import { cn } from "@fieldcheck/shared";

/**
 * Lightweight, dependency-free, theme-aware charts.
 *
 * Everything is plain SVG/HTML + CSS variables so it inherits the active theme
 * (no Recharts/Chart.js bundle). Colors are passed as Tailwind utility classes
 * mapped to design tokens (e.g. "bg-primary", "text-warning"). Charts are
 * accessible: each provides a visually-hidden data table via aria, and bars use
 * role="img" with a descriptive label.
 */

export interface ChartDatum {
  /** Unique key (used for For). */
  key: string;
  /** Visible category label. */
  label: string;
  value: number;
  /** Tailwind class for the fill (defaults to primary). */
  colorClass?: string;
  /** Optional click handler — makes the segment/bar interactive. */
  onSelect?: () => void;
}

const DEFAULT_COLOR = "bg-primary";

function formatPct(value: number, total: number): string {
  if (total <= 0) {
    return "0%";
  }
  return `${Math.round((value / total) * 100)}%`;
}

/** Accessible, visually-hidden table mirroring the chart data for screen readers. */
function DataTable(props: { caption: string; data: ChartDatum[]; total: number }) {
  return (
    <table class="sr-only">
      <caption>{props.caption}</caption>
      <thead>
        <tr>
          <th scope="col">Category</th>
          <th scope="col">Value</th>
          <th scope="col">Share</th>
        </tr>
      </thead>
      <tbody>
        <For each={props.data}>
          {(d) => (
            <tr>
              <th scope="row">{d.label}</th>
              <td>{d.value}</td>
              <td>{formatPct(d.value, props.total)}</td>
            </tr>
          )}
        </For>
      </tbody>
    </table>
  );
}

interface BarChartProps {
  title: string;
  data: ChartDatum[];
  /** Accessible chart description (caption). */
  caption?: string;
  class?: string;
  /** Hide the chart and render an empty hint when there's no data. */
  emptyHint?: JSX.Element;
}

/**
 * Horizontal bar chart. Bars are scaled to the max value so the tallest bar
 * fills the track. Each row shows label + value and an animated bar.
 */
export function BarChart(props: BarChartProps) {
  const max = createMemo(() => Math.max(1, ...props.data.map((d) => d.value)));
  const total = createMemo(() => props.data.reduce((sum, d) => sum + d.value, 0));
  const hasData = createMemo(() => total() > 0);

  return (
    <div class={cn("w-full", props.class)}>
      <Show when={hasData()} fallback={props.emptyHint}>
        <div role="img" aria-label={props.caption ?? props.title} class="flex flex-col gap-2.5">
          <For each={props.data}>
            {(d) => {
              const pct = () => `${Math.round((d.value / max()) * 100)}%`;
              const row = (
                <div class="flex items-center gap-3">
                  <span class="w-24 shrink-0 truncate text-xs font-medium text-muted-foreground sm:w-28">
                    {d.label}
                  </span>
                  <div class="relative h-5 flex-1 overflow-hidden rounded-full bg-muted/40">
                    <div
                      class={cn(
                        "h-full rounded-full transition-[width] duration-500 ease-out",
                        d.colorClass ?? DEFAULT_COLOR,
                      )}
                      style={{ width: d.value > 0 ? pct() : "0%" }}
                    />
                  </div>
                  <span class="w-8 shrink-0 text-right text-xs font-bold tabular-nums">
                    {d.value}
                  </span>
                </div>
              );
              return (
                <Show when={d.onSelect} fallback={row}>
                  <button
                    type="button"
                    class="rounded-md text-left transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    onClick={() => d.onSelect?.()}
                    aria-label={`${d.label}: ${d.value}`}
                  >
                    {row}
                  </button>
                </Show>
              );
            }}
          </For>
        </div>
        <DataTable caption={props.caption ?? props.title} data={props.data} total={total()} />
      </Show>
    </div>
  );
}

const DONUT_SIZE = 120;
const DONUT_STROKE = 16;
const DONUT_RADIUS = (DONUT_SIZE - DONUT_STROKE) / 2;
const DONUT_CIRC = 2 * Math.PI * DONUT_RADIUS;

interface DonutChartProps {
  title: string;
  data: ChartDatum[];
  /** Big number rendered in the center (e.g. total). */
  centerValue?: string | number;
  centerLabel?: string;
  caption?: string;
  class?: string;
  emptyHint?: JSX.Element;
}

/**
 * Donut/pie chart drawn with stacked stroke-dasharray arcs. Color classes are
 * mapped to a `text-*` token and applied via `stroke="currentColor"`, so each
 * segment must carry a `colorClass` like "text-warning".
 */
export function DonutChart(props: DonutChartProps) {
  const total = createMemo(() => props.data.reduce((sum, d) => sum + d.value, 0));
  const hasData = createMemo(() => total() > 0);

  const segments = createMemo(() => {
    let offset = 0;
    return props.data
      .filter((d) => d.value > 0)
      .map((d) => {
        const fraction = d.value / total();
        const dash = fraction * DONUT_CIRC;
        const seg = { datum: d, dash, offset, fraction };
        offset += dash;
        return seg;
      });
  });

  return (
    <div class={cn("w-full", props.class)}>
      <Show when={hasData()} fallback={props.emptyHint}>
        <div class="flex flex-col items-center gap-4 sm:flex-row sm:justify-center sm:gap-6">
          <div class="relative shrink-0" role="img" aria-label={props.caption ?? props.title}>
            <svg
              width={DONUT_SIZE}
              height={DONUT_SIZE}
              viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
              class="-rotate-90"
            >
              {/* Track */}
              <circle
                cx={DONUT_SIZE / 2}
                cy={DONUT_SIZE / 2}
                r={DONUT_RADIUS}
                fill="none"
                class="text-muted/40"
                stroke="currentColor"
                stroke-width={DONUT_STROKE}
              />
              <For each={segments()}>
                {(s) => (
                  <circle
                    cx={DONUT_SIZE / 2}
                    cy={DONUT_SIZE / 2}
                    r={DONUT_RADIUS}
                    fill="none"
                    class={cn(s.datum.colorClass ?? "text-primary", "transition-all duration-500")}
                    stroke="currentColor"
                    stroke-width={DONUT_STROKE}
                    stroke-dasharray={`${s.dash} ${DONUT_CIRC - s.dash}`}
                    stroke-dashoffset={-s.offset}
                    stroke-linecap="butt"
                  />
                )}
              </For>
            </svg>
            <Show when={props.centerValue !== undefined}>
              <div class="absolute inset-0 flex flex-col items-center justify-center">
                <span class="text-2xl font-black tabular-nums">{props.centerValue}</span>
                <Show when={props.centerLabel}>
                  <span class="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    {props.centerLabel}
                  </span>
                </Show>
              </div>
            </Show>
          </div>

          {/* Legend */}
          <ul class="flex flex-col gap-1.5">
            <For each={props.data.filter((d) => d.value > 0)}>
              {(d) => (
                <li class="flex items-center gap-2 text-xs">
                  <span
                    class={cn(
                      "h-2.5 w-2.5 shrink-0 rounded-full bg-current",
                      d.colorClass ?? "text-primary",
                    )}
                  />
                  <span class="font-medium">{d.label}</span>
                  <span class="font-bold tabular-nums text-muted-foreground">
                    {d.value} · {formatPct(d.value, total())}
                  </span>
                </li>
              )}
            </For>
          </ul>
        </div>
        <DataTable caption={props.caption ?? props.title} data={props.data} total={total()} />
      </Show>
    </div>
  );
}

interface ProgressMeterProps {
  /** 0..100 */
  percentage: number;
  label: string;
  colorClass?: string;
  class?: string;
}

/** A single horizontal progress meter for rates (e.g. approval / compliance). */
export function ProgressMeter(props: ProgressMeterProps) {
  const pct = createMemo(() => Math.max(0, Math.min(100, Math.round(props.percentage))));
  return (
    <div class={cn("w-full", props.class)}>
      <div class="mb-1 flex items-baseline justify-between">
        <span class="text-xs font-medium text-muted-foreground">{props.label}</span>
        <span class="text-sm font-black tabular-nums">{pct()}%</span>
      </div>
      <div
        class="h-2.5 w-full overflow-hidden rounded-full bg-muted/40"
        role="progressbar"
        aria-valuenow={pct()}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={props.label}
      >
        <div
          class={cn(
            "h-full rounded-full transition-[width] duration-500 ease-out",
            props.colorClass ?? "bg-success",
          )}
          style={{ width: `${pct()}%` }}
        />
      </div>
    </div>
  );
}
